<template>
  <div class="calendar-events-timeline-holder">
    <v-container class="py-0">
      <v-row>
        <v-col cols="4" class="px-2 pt-1 pb-0 filters-container">
          <v-select
            v-model="selectedCategory"
            :items="categories"
            item-text="text"
            item-value="value"
            label="Category"
            hide-details
            multiple
            class="py-0 filters-label"
            @change="handleChangeFilter"
          >
            <template v-slot:selection="{ index }">
              <span v-if="index === 0">
                {{ selectedCategory.length }} selected
              </span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="4" class="px-2 pt-1 pb-0">
          <v-select
            v-model="selectedSeverity"
            :items="severities"
            item-text="text"
            item-value="value"
            label="Potential Severity"
            hide-details
            multiple
            class="py-0 filters-label"
            @change="handleChangeFilter"
          >
            <template v-slot:selection="{ index }">
              <span v-if="index === 0">
                {{ selectedSeverity.length }} selected
              </span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="4" class="px-2 pt-1 pb-0">
          <v-select
            v-model="selectedImpactType"
            :items="impactTypes"
            item-text="text"
            item-value="value"
            label="Impact Type"
            hide-details
            multiple
            class="py-0 filters-label"
            @change="handleChangeFilter"
          >
            <template v-slot:selection="{ index }">
              <span v-if="index === 0">
                {{ selectedImpactType.length }} selected
              </span>
            </template>
          </v-select>
        </v-col>
      </v-row>
    </v-container>
    <Loader v-if="loading && items.length < 1" text="" color="black" />
    <v-container
      class="timeline-container mt-3 pt-0"
      :class="{ 'fixed-height': fixedHeight }"
    >
      <v-row>
        <v-col class="pt-0">
          <CalendarEventsTimeline
            v-if="items && items.length"
            class="mr-4"
            :items="itemsPlus"
            :pageSize="pageSize"
            :page="page"
            :heightPixels="height"
            :infiniteLoadReset="infiniteLoadReset"
            :openedTimestamp="openedTimestamp"
            :scrollToTop="scrollToTop"
            @get-data="getContent"
          />
          <p
            v-if="(!items || !items.length) && !loading"
            class="text-center mt-6"
          >
            <span v-if="!this.params || !this.params.aoiId"
              >No {{ title }} available.</span
            >
            <span v-if="this.params && this.params.aoiId"
              >There are no {{ title }} for this location.</span
            >
          </p>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import lodashDebounce from "lodash/debounce";
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashOrderBy from "lodash/orderBy";

import {
  getCalendarEvents,
  getCalendarEventsCategories,
  getCalendarEventsImpactTypes,
} from "@/services/calendarEvents/calendarEvents.service";
import { LABELS } from "@/common/constants/labels";

export default {
  name: "CalendarEventsTimelineHolder",
  components: {
    CalendarEventsTimeline: () =>
      import("@/components/calendarEvents/CalendarEventsTimeline"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
    openedTimestamp: {
      type: String,
    },
    fixedHeight: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      items: [],
      pageSize: 10,
      page: 1,
      infiniteLoadReset: null,
      loading: false,
      scrollToTop: null,
      lastDate: null,
      paramsLastChanged: null,
      selectedCategory: null,
      categories: [],
      selectedSeverity: null,
      severities: [
        { text: "Low", value: "low" },
        { text: "Medium", value: "medium" },
        { text: "High", value: "high" },
      ],
      selectedImpactType: null,
      impactTypes: [],
    };
  },
  async mounted() {
    this.categories = await getCalendarEventsCategories();
    this.impactTypes = await getCalendarEventsImpactTypes();
    await this.getContent();
  },
  computed: {
    itemsPlus() {
      return lodashOrderBy(
        this.items.map((item) => {
          let date = lodashGet(
            item,
            "properties.formatted.eventTimelineStartDate"
          );
          if (date !== this.lastDate) {
            item.showDate = true;
            this.lastDate = date;
          } else {
            item.showDate = false;
          }
          return item;
        }),
        ["properties.formatted.eventTimelineStartDate"],
        ["asc"]
      );
    },
    title() {
      return LABELS.calendarEvents;
    },
    height() {
      return this.$parent.$el.clientHeight;
    },
  },
  methods: {
    async getContent(page, $state, params) {
      this.loading = true;
      if (!page) {
        this.scrollToTop = new Date().toISOString();
      }
      await this.getContentDebounced(page, $state, params);
    },
    getContentDebounced: lodashDebounce(async function (page, $state, params) {
      let result;
      let dataParams;
      let shouldResetInfiniteLoad = true;
      let paramsLastChanged = this.paramsLastChanged;
      try {
        this.page = isNaN(page) ? 1 : page;
        dataParams = this.getParams(params) || {};
        result = await getCalendarEvents(dataParams);

        //Only update the items if our search params haven't changed since we sent the search
        if (this.paramsLastChanged === paramsLastChanged) {
          if (!result || result.features.length < 1) {
            shouldResetInfiniteLoad = false;
          } else {
            this.items = this.items.concat(result.features);
          }
        }
      } catch (error) {
        console.error("Error in getContent: " + JSON.stringify(error));
        console.error(error);
      } finally {
        //Only reset the loading and infinit loading if our search params haven't changed since we sent the search
        if (this.paramsLastChanged === paramsLastChanged) {
          this.loading = false;
          this.infiniteLoadReset = shouldResetInfiniteLoad
            ? new Date().toISOString()
            : null;
        }

        result = null;
        dataParams = null;
        paramsLastChanged = null;
      }
    }, 500),
    getParams() {
      let paramsCopy = lodashCloneDeep(this.params);

      paramsCopy.pageSize = this.pageSize;
      paramsCopy.pageOffset = (this.page - 1) * this.pageSize;

      //TODO if this component gets used for other data types remove this code
      // and apply it to parameters before they get set.
      // right now its just a clean way to avoid extra http requests.
      paramsCopy.start = new dayjs().startOf("day").toISOString();
      paramsCopy.end = new dayjs().add(5, "years").endOf("day").toISOString();
      paramsCopy.sortDirection = "asc";

      if (this.selectedCategory && this.selectedCategory.length) {
        paramsCopy.categories = this.selectedCategory;
      }
      if (this.selectedSeverity && this.selectedSeverity.length) {
        paramsCopy.severity = this.selectedSeverity;
      }
      if (this.selectedImpactType && this.selectedImpactType.length) {
        paramsCopy.impacts = this.selectedImpactType;
      }

      if (
        (paramsCopy.regionFilters && paramsCopy.regionFilters.length) ||
        (paramsCopy.countryFilters && paramsCopy.countryFilters.length)
      ) {
        paramsCopy.aoiId = paramsCopy.regionFilters.concat(
          paramsCopy.countryFilters
        );
      }

      // delete unneeded props
      delete paramsCopy.severityMin;
      delete paramsCopy.severityMax;
      delete paramsCopy.bounds;
      delete paramsCopy.boundsType;
      delete paramsCopy.allRegionsSelected;

      return paramsCopy;
    },
    resetItems() {
      this.items = [];
    },
    resetState() {
      this.resetItems();
      this.page = 1;
      this.lastDate = null;
    },
    async handleChangeFilter() {
      //Save the timestamp of this specific params
      this.paramsLastChanged = new Date().toISOString();

      this.resetState();
      await this.getContent();
    },
  },
  watch: {
    params: {
      async handler() {
        //Save the timestamp of this specific params
        this.paramsLastChanged = new Date().toISOString();

        this.resetState();
        this.getContent();
      },
      deep: true,
    },
    openedTimestamp() {
      setTimeout(() => {
        this.height = this.$parent.$el.clientHeight;
      });
    },
  },
  beforeDestroy() {
    this.selectedCategory = null;
    this.categories = [];
    this.selectedSeverity = null;
    this.severities = [];
    this.selectedImpactType = null;
    this.impactTypes = [];
  },
};
</script>

<style>
.calendar-events-timeline-holder {
  display: grid;
}
.filters-label .v-label,
.filters-label .v-select__selections {
  font-size: 0.75rem;
}
.area-container .timeline-container {
  overflow-y: auto;
}
.area-container .timeline-container.fixed-height {
  height: calc((100vh - 60px) / 2 - 90px);
}
</style>
